/**
 * 52. Supplier Evaluation — scorecards; on-time, quality, price
 * variance; risk.
 *
 * Arabic and English (C7): every word from the catalogs; scores are numbers
 * and percentages the locale writes; a risk level is a word.
 *
 * Read only. Its "New evaluation" button had no handler and was removed
 * (UI-HONEST): no plan builds supplier evaluations, and the one route that
 * takes scores (POST /v1/suppliers/:id/actions/evaluate) writes an audit row
 * that this list never reads, so wiring it would have shown a success that
 * changes nothing here.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatPercent, formatQuantity } from "@/lib/money";

interface SupplierEvaluationRow {
  id: string;
  supplierName: string;
  period: string;
  onTimeDelivery: string;
  qualityScore: string;
  priceVariance: string;
  riskLevel: "Low" | "Medium" | "High" | "Critical";
  overallScore: string;
}

export function SupplierEvaluationPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<SupplierEvaluationRow>(
    ["procurement", "supplier-evaluation"],
    "/procurement/supplier-evaluations",
  );

  return (
    <div>
      <PageHeader
        title={t("procurement.evaluations.title")}
        subtitle={t("procurement.evaluations.subtitle")}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("procurement.evaluations.list"), data.total)}>
          <DataTable<SupplierEvaluationRow>
            rows={data.items}
            columns={[
              { key: "supplierName", label: t("fields.supplier") },
              { key: "period", label: t("fields.period") },
              {
                key: "onTimeDelivery",
                label: t("procurement.evaluations.onTime"),
                numeric: true,
                render: (r) => formatPercent(r.onTimeDelivery),
              },
              {
                key: "qualityScore",
                label: t("procurement.evaluations.quality"),
                numeric: true,
                render: (r) => formatPercent(r.qualityScore),
              },
              {
                key: "priceVariance",
                label: t("procurement.evaluations.priceVariance"),
                numeric: true,
                render: (r) => formatPercent(r.priceVariance),
              },
              {
                key: "overallScore",
                label: t("procurement.evaluations.overall"),
                numeric: true,
                render: (r) => (
                  <strong>{formatQuantity(r.overallScore, 2)}</strong>
                ),
              },
              {
                key: "riskLevel",
                label: t("fields.risk"),
                render: (r) => (
                  <Badge
                    tone={
                      r.riskLevel === "Critical"
                        ? "danger"
                        : r.riskLevel === "High"
                          ? "danger"
                          : r.riskLevel === "Medium"
                            ? "warning"
                            : "success"
                    }
                  >
                    {valueLabel(t, "risk", r.riskLevel)}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
